"use client";

import React from "react";
import { Star, Sparkles, WandSparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const steps = [{
  id: 1,
  title: "Summon the Magic",
  description: "Reach out via our booking form or give us a ring. Tell us about your event, your vision, and your guests. We'll discuss your needs and confirm availability instantly.",
  icon: WandSparkles
}, {
  id: 2,
  title: "We Craft the Spell",
  description: "Sit back while we weave the details. From customizing the creative experience to coordinating logistics, we handle the preparation so you don't have to lift a finger.",
  icon: Sparkles
}, {
  id: 3,
  title: "Experience the Wonder",
  description: "On the big day, our team arrives fully equipped to deliver a seamless, stress-free performance. You get to relax, enjoy the compliments, and make magical memories.",
  icon: Star
}];
export default function TheStressFreeSpell() {
  const {
    ref: sectionRef,
    isVisible
  } = useRevealOnScroll();
  return <section id="the-stress-free-spell" ref={sectionRef} className="text-primary-foreground relative overflow-hidden py-24 lg:py-32 font-sans text-lg">
      {}
      <div className="pointer-events-none absolute inset-0 z-0 overflow-hidden">
        <div className="animate-[spin_60s_linear_infinite] absolute -top-[20%] -left-[10%] h-[800px] w-[800px] rounded-full bg-secondary/10 blur-[100px] opacity-40 mix-blend-screen" />
        <div className="animate-[spin_50s_linear_infinite_reverse] absolute top-[40%] -right-[10%] h-[600px] w-[600px] rounded-full bg-secondary/5 blur-[80px] opacity-30 mix-blend-screen" />
        <div className="animate-[pulse_10s_ease-in-out_infinite] absolute bottom-0 left-1/3 h-[500px] w-[500px] rounded-full bg-secondary/15 blur-[120px] opacity-40" />
      </div>

      <div className="container relative z-10 mx-auto px-4 sm:px-6">
        {}
        <div className="mb-20 text-center">
          <h2 className="font-serif text-5xl md:text-6xl lg:text-7xl font-normal text-secondary mb-6 drop-shadow-lg">
            The Stress-Free Spell
          </h2>
          <p className="mx-auto max-w-2xl text-xl !text-[hsl(0,0%,100%)] !italic lg:text-2xl">Booking your magical experience is as easy as 1-2-3. <br />Follow our simple path to an unforgettable event.</p>
        </div>

        {}
        <div className="relative mx-auto max-w-5xl">
          {}
          <div className="absolute left-1/2 top-0 hidden h-full w-full -translate-x-1/2 md:block pointer-events-none">
            <svg className="h-full w-full" viewBox="0 0 400 800" fill="none" preserveAspectRatio="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M200,0 C200,100 100,150 100,250 C100,350 300,450 300,550 C300,650 200,700 200,800" stroke="url(#glow-gradient)" strokeWidth="4" strokeDasharray="10 10" className="opacity-40" />
              <defs>
                <linearGradient id="glow-gradient" x1="200" y1="0" x2="200" y2="800" gradientUnits="userSpaceOnUse">
                  <stop offset="0%" stopColor="var(--primary)" stopOpacity="0" />
                  <stop offset="50%" stopColor="var(--secondary)" stopOpacity="0.8" />
                  <stop offset="100%" stopColor="var(--primary)" stopOpacity="0" />
                </linearGradient>
              </defs>
            </svg>
          </div>

          {}
          <div className="space-y-16 md:space-y-24">
            {steps.map((step, index) => {
            const isEven = index % 2 !== 0;
            return <div key={step.id} data-index={index} className={`relative flex flex-col items-center gap-8 md:flex-row ${isEven ? "md:flex-row-reverse" : ""} transition-all duration-1000 ease-out`} style={{
              opacity: isVisible ? 1 : 0,
              transform: isVisible ? "translateY(0)" : `translateY(${50 + index * 20}px)`
            }}>
                  {}
                  <div className="relative flex-shrink-0">
                    <div className="relative flex h-32 w-32 items-center justify-center rounded-full border-2 border-secondary/30 bg-white/10 backdrop-blur-sm shadow-[0_0_30px_-5px_var(--secondary)]">
                      <span className="font-serif text-8xl font-bold text-transparent bg-clip-text bg-gradient-to-b from-secondary to-secondary/20 stroke-secondary" style={{
                    WebkitTextStroke: "1px var(--secondary)"
                  }}>
                        {step.id}
                      </span>
                      <div className="absolute -right-2 -top-2 rounded-full bg-secondary p-2 text-primary shadow-lg">
                        <step.icon className="h-6 w-6" />
                      </div>
                    </div>
                  </div>

                  {}
                  <div className={`flex flex-col text-center md:w-1/2 ${isEven ? "md:text-right md:items-end" : "md:text-left md:items-start"}`}>
                    <h3 className="mb-3 font-serif font-normal text-secondary !text-5xl">
                      {step.title}
                    </h3>
                    <p className="leading-relaxed !text-xl !text-[hsl(0,0%,100%)]">
                      {step.description}
                    </p>
                  </div>
                  
                  {}
                  <div className="hidden md:block md:w-1/2" />
                </div>;
          })}
          </div>
        </div>

        {}
        <div className="mt-24 text-center">
          <Button asChild size="lg" className="bg-secondary text-secondary-foreground hover:bg-secondary/90 text-lg px-8 py-6 rounded-full shadow-[0_0_20px_-5px_var(--secondary)] transition-transform hover:scale-105">
            <Link to="#summon-the-magic" className="inline-flex items-center justify-center gap-2 whitespace-nowrap font-medium [&_svg]:pointer-events-none [&_svg:not([class*='size-'])]:size-4 shrink-0 [&_svg]:shrink-0 outline-none focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] aria-invalid:ring-destructive/20 aria-invalid:border-destructive h-10 has-[>svg]:px-4 bg-secondary text-secondary-foreground px-8 py-6 rounded-full shadow-[0_0_20px_-5px_var(--secondary)] transition-transform !text-xl disabled:pointer-events-none disabled:opacity-50 dark:aria-invalid:ring-destructive/40 hover:bg-secondary/90 hover:scale-105">Start Your Magical Journey</Link>
          </Button>
        </div>
      </div>
    </section>;
}